import { useLocation } from 'react-router-dom';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import './ReaderFileLibrary.scss';
import { useEffect, useMemo, useState } from 'react';
import useAuth from '@hooks/useAuth';
import CustomLoader from '@components/UI/CustomLoader/CustomLoader';
import { getAuthUserId } from '@modules/OnlineLibrary/helpers/getAuthUserId';
import { getLibraryRawFileUrl } from '@modules/OnlineLibrary/helpers/getLibraryFileUrl';

const ReaderFileLibrary = () => {
  const { user } = useAuth();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search.replace(/\+/g, '%2B')),
    [location.search]
  );
  const uuid = searchParams.get('uuid');
  const contentType = searchParams.get('contentType');
  const userIdFromQuery = searchParams.get('user_id');

  const [fileUrl, setFileUrl] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Онлайн библиотека';
  }, []);

  useEffect(() => {
    if (!uuid) {
      setFileUrl(null);
      return;
    }

    const queryUserId =
      userIdFromQuery && userIdFromQuery !== 'undefined'
        ? userIdFromQuery
        : undefined;
    const userId = queryUserId ?? getAuthUserId(user);

    if (!userId) {
      setFileUrl(null);
      return;
    }

    setFileUrl(getLibraryRawFileUrl(uuid, userId));
  }, [uuid, userIdFromQuery, user]);

  const getExtensionFromMimeType = (mimeType) => {
    switch (mimeType) {
      case 'application/msword':
        return 'doc';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'docx';
      case 'application/pdf':
        return 'pdf';
      case 'image/png':
        return 'png';
      case 'image/jpeg':
        return 'jpeg';
      case 'image/jpg':
        return 'jpg';
      case 'image/webp':
        return 'webp';
      case 'image/gif':
        return 'gif';
      case 'image/tiff':
        return 'tiff';
      case 'application/vnd.ms-excel':
        return 'xls';
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'xlsx';
      case 'application/vnd.ms-excel.sheet.macroEnabled.12':
        return 'xlsm';
      case 'video/mp4':
        return 'mp4';
      case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        return 'pptx';
      default:
        return '';
    }
  };

  if (!uuid || !fileUrl) {
    return (
      <div className="d-flex justify-center align-center" style={{ minHeight: '100vh' }}>
        <CustomLoader />
      </div>
    );
  }

  const fileType = getExtensionFromMimeType(contentType);

  const extensions = {
    png: 'png',
    jpg: 'jpg',
    jpeg: 'jpeg',
    webp: 'webp',
  };

  if (extensions[fileType]) {
    return <img src={fileUrl} width="100%" height="100%" alt="image" />;
  }

  if (fileType === 'pdf' || fileType === 'mp4') {
    return (
      <div className="pdf-viewer">
        <iframe
          src={fileUrl}
          title="Viewer"
          width="100%"
          height="100%"
          className="iframe"
        />
      </div>
    );
  }

  const docs = [
    {
      uri: fileUrl,
      fileType,
    },
  ];

  return (
    <div className="doc-viewer">
      <DocViewer documents={docs} pluginRenderers={DocViewerRenderers} />
    </div>
  );
};

export default ReaderFileLibrary;
